import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import ItemServiceApi, { PaginatedItemsResponse } from '../../Api/item-service';
import { VendorCatalogItem } from './types';
import { ExtractFilterType } from '../Filters/useParamFilter';
import { possibleParams } from './params.constants';
import { VPStages } from '../../../Components/SellToUs/enums/stage.enums';
import { ItemFilters } from './enum';

const DEFAULT_LIMIT = 20;

export const vpStagesToFilter = (stage: VPStages) => {
  switch (stage) {
    case VPStages.PENDING_FN:
      return `stage::in::${[ItemFilters.NEEDS_REVIEW, ItemFilters.ACCEPTED].join(',')}`;
    case VPStages.NEEDS_DETAILS:
      return `stage::eq::${ItemFilters.PENDING_VENDOR}`;
    case VPStages.DETAILS_SENT:
      return `stage::in::${[ItemFilters.PO_DRAFTS, ItemFilters.MANAGER_REVIEW, ItemFilters.FINAL_REVIEW].join(',')}`;
    case VPStages.PO_CREATED:
      return `stage::eq::${ItemFilters.PO_CREATED}`;
    case VPStages.PASSED:
      return `stage::eq::${ItemFilters.REJECTED}`;
    default:
      return undefined;
  }
};

type PaginatedResponse = PaginatedItemsResponse<VendorCatalogItem>;

type GetVendorItemsParams = {
  paramFilters: ExtractFilterType<typeof possibleParams>;
  limit?: number;
};

export const useGetVendorItems = (
  userId: string,
  { paramFilters, limit = DEFAULT_LIMIT }: GetVendorItemsParams,
  options?: Omit<
    UseInfiniteQueryOptions<PaginatedResponse, unknown>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >,
) => {
  const filters: string[] = [];

  if (paramFilters.stage) {
    const filter = vpStagesToFilter(paramFilters.stage);
    if (filter) {
      filters.push(filter);
    }
  }

  if (paramFilters.search) {
    filters.push(`style_number::ilike::${paramFilters.search}`);
  }

  if (paramFilters.category?.length) {
    filters.push(`category::in::${paramFilters.category.join(',')}`);
  }

  if (paramFilters.class?.length) {
    filters.push(`class::in::${paramFilters.class.join(',')}`);
  }

  const query = useInfiniteQuery<PaginatedResponse>(
    ['item-service-vendor-catalog', userId, paramFilters, limit],
    async ({ pageParam }): Promise<PaginatedResponse> =>
      ItemServiceApi.getVendorItems<VendorCatalogItem>(userId, {
        filter: filters.join(';'),
        limit,
        cursor: pageParam as string | undefined,
      }),
    {
      ...options,
      getNextPageParam: (lastPage) => {
        if (lastPage.hasMore && lastPage.nextCursor) {
          return lastPage.nextCursor;
        }
        return undefined;
      },
    },
  );

  const items: VendorCatalogItem[] =
    query.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    ...query,
    items,
  };
};
